import { supabase } from '../lib/supabase';

// ============================================================
// PROJECTS SERVICE
// ============================================================

const toProjectRow = (data) => ({
  project_name: data?.projectName,
  client: data?.client,
  location: data?.location,
  project_type: data?.projectType || 'Telecom',
  current_version: data?.currentVersion || 'v1.0',
  status: data?.status || 'draft',
  created_date: data?.createdDate || new Date()?.toISOString()?.split('T')?.[0],
  last_modified: new Date()?.toISOString()?.split('T')?.[0],
  estimator: data?.estimator || null,
  bdm: data?.bdm || null,
  contract_type: data?.contractType || null,
  duration: data?.duration || 0,
  total_cost: data?.totalCost || 0,
  sell_price: data?.sellPrice || 0,
  margin: data?.margin || 0,
  description: data?.description || null,
  vat_percentage: data?.vatPercentage || 15,
  retention_percentage: data?.retentionPercentage || 10,
  bond_percentage: data?.bondPercentage || 5,
  warranty_months: data?.warrantyMonths || 12,
  payment_terms: data?.paymentTerms || null,
  advance_payment: data?.advancePayment || 30,
  milestone_payment: data?.milestonePayment || 60,
  final_payment: data?.finalPayment || 10,
  margin_percentage: data?.marginPercentage || 20,
  overhead_percentage: data?.overheadPercentage || 15,
  contingency_percentage: data?.contingencyPercentage || 10,
  escalation_percentage: data?.escalationPercentage || 5,
  finance_cost_percentage: data?.financeCostPercentage || 3,
  currency: data?.currency || 'USD',
  module: data?.module || null,
  resources: data?.resources || [],
});

const fromProjectRow = (row) => ({
  id: row?.id,
  projectName: row?.project_name,
  client: row?.client,
  location: row?.location,
  projectType: row?.project_type,
  currentVersion: row?.current_version,
  status: row?.status,
  createdDate: row?.created_date,
  lastModified: row?.last_modified,
  estimator: row?.estimator,
  bdm: row?.bdm,
  contractType: row?.contract_type,
  duration: row?.duration,
  totalCost: parseFloat(row?.total_cost) || 0,
  sellPrice: parseFloat(row?.sell_price) || 0,
  margin: parseFloat(row?.margin) || 0,
  description: row?.description,
  vatPercentage: row?.vat_percentage,
  retentionPercentage: row?.retention_percentage,
  bondPercentage: row?.bond_percentage,
  warrantyMonths: row?.warranty_months,
  paymentTerms: row?.payment_terms,
  advancePayment: row?.advance_payment,
  milestonePayment: row?.milestone_payment,
  finalPayment: row?.final_payment,
  marginPercentage: row?.margin_percentage,
  overheadPercentage: row?.overhead_percentage,
  contingencyPercentage: row?.contingency_percentage,
  escalationPercentage: row?.escalation_percentage,
  financeCostPercentage: row?.finance_cost_percentage,
  currency: row?.currency,
  module: row?.module,
  resources: row?.resources || [],
  userId: row?.user_id,
  createdAt: row?.created_at,
  updatedAt: row?.updated_at,
});

export const getProjects = async (userId = null) => {
  let query = supabase?.from('projects')?.select('*')?.order('created_at', { ascending: false });
  if (userId) {
    query = query?.eq('user_id', userId);
  }
  const { data, error } = await query;
  if (error) throw error;
  return (data || [])?.map(fromProjectRow);
};

export const getProject = async (id) => {
  const { data, error } = await supabase?.from('projects')?.select('*')?.eq('id', id)?.single();
  if (error) throw error;
  return fromProjectRow(data);
};

export const createProject = async (projectData, userId) => {
  const { data, error } = await supabase?.from('projects')?.insert({ ...toProjectRow(projectData), user_id: userId })?.select()?.single();
  if (error) throw error;
  return fromProjectRow(data);
};

export const updateProject = async (id, projectData) => {
  const { data, error } = await supabase?.from('projects')?.update(toProjectRow(projectData))?.eq('id', id)?.select()?.single();
  if (error) throw error;
  return fromProjectRow(data);
};

export const deleteProject = async (id) => {
  const { error } = await supabase?.from('projects')?.delete()?.eq('id', id);
  if (error) throw error;
  return true;
};

// ============================================================
// BOQ ITEMS SERVICE
// ============================================================

const toBOQRow = (item, userId, projectId, sortOrder) => ({
  user_id: userId,
  project_id: projectId || null,
  line_number: item?.lineNumber || '',
  item_code: item?.itemCode || '',
  description: item?.description || '',
  module: item?.module || 'material',
  vendor_spec: item?.vendorSpec || '',
  uom: item?.uom || 'ea',
  quantity: parseFloat(item?.quantity) || 0,
  budget_unit_cost: parseFloat(item?.budgetUnitCost) || 0,
  waste_percent: parseFloat(item?.wastePercent) || 0,
  productivity_units: parseFloat(item?.productivityUnits) || 0,
  lead_time_days: parseInt(item?.leadTimeDays) || 0,
  needs_quote: item?.needsQuote || 'no',
  preferred_vendor: item?.preferredVendor || '',
  sort_order: sortOrder || 0,
});

const fromBOQRow = (row) => ({
  id: row?.id,
  lineNumber: row?.line_number,
  itemCode: row?.item_code,
  description: row?.description,
  module: row?.module,
  vendorSpec: row?.vendor_spec,
  uom: row?.uom,
  quantity: String(row?.quantity),
  budgetUnitCost: String(row?.budget_unit_cost),
  wastePercent: String(row?.waste_percent),
  productivityUnits: String(row?.productivity_units),
  leadTimeDays: String(row?.lead_time_days),
  needsQuote: row?.needs_quote,
  preferredVendor: row?.preferred_vendor,
  sortOrder: row?.sort_order,
  projectId: row?.project_id,
  userId: row?.user_id,
});

export const getBOQItems = async (projectId) => {
  let query = supabase?.from('boq_items')?.select('*');
  if (projectId) {
    query = query?.eq('project_id', projectId);
  }
  const { data, error } = await query?.order('sort_order', { ascending: true });
  if (error) throw error;
  return (data || [])?.map(fromBOQRow);
};

export const saveBOQItems = async (items, userId, projectId) => {
  if (!items || items?.length === 0) return [];

  // Delete existing items for this project/user
  if (projectId) {
    await supabase?.from('boq_items')?.delete()?.eq('project_id', projectId)?.eq('user_id', userId);
  }

  const rows = items?.map((item, index) => toBOQRow(item, userId, projectId, index));
  const { data, error } = await supabase?.from('boq_items')?.insert(rows)?.select();
  if (error) throw error;
  return (data || [])?.map(fromBOQRow);
};

export const addBOQItem = async (item, userId, projectId) => {
  const { count } = await supabase?.from('boq_items')?.select('*', { count: 'exact', head: true })?.eq('user_id', userId);

  const { data, error } = await supabase?.from('boq_items')?.insert(toBOQRow(item, userId, projectId, count || 0))?.select()?.single();
  if (error) throw error;
  return fromBOQRow(data);
};

export const updateBOQItem = async (id, item) => {
  const { data, error } = await supabase?.from('boq_items')?.update({
      line_number: item?.lineNumber,
      item_code: item?.itemCode,
      description: item?.description,
      module: item?.module,
      vendor_spec: item?.vendorSpec,
      uom: item?.uom,
      quantity: parseFloat(item?.quantity) || 0,
      budget_unit_cost: parseFloat(item?.budgetUnitCost) || 0,
      waste_percent: parseFloat(item?.wastePercent) || 0,
      productivity_units: parseFloat(item?.productivityUnits) || 0,
      lead_time_days: parseInt(item?.leadTimeDays) || 0,
      needs_quote: item?.needsQuote,
      preferred_vendor: item?.preferredVendor,
    })?.eq('id', id)?.select()?.single();
  if (error) throw error;
  return fromBOQRow(data);
};

export const deleteBOQItem = async (id) => {
  const { error } = await supabase?.from('boq_items')?.delete()?.eq('id', id);
  if (error) throw error;
  return true;
};

// ============================================================
// ESTIMATES SERVICE
// ============================================================

const toEstimateRow = (data, userId) => ({
  user_id: userId,
  name: data?.name,
  calc_number: data?.calcNumber || null,
  ptc_number: data?.ptcNumber || null,
  project_name: data?.projectName || null,
  notes: data?.notes || null,
  delivery_mode: data?.deliveryMode || 'onsite',
  selected_engineers: data?.selectedEngineers || [],
  selected_licenses: data?.selectedLicenses || [],
  selected_materials: data?.selectedMaterials || [],
  selected_outsource: data?.selectedOutsource || [],
  config: data?.config || {},
  currency: data?.currency || 'OMR',
  calculations: data?.calculations || {},
});

const fromEstimateRow = (row) => ({
  id: row?.id,
  name: row?.name,
  calcNumber: row?.calc_number,
  ptcNumber: row?.ptc_number,
  projectName: row?.project_name,
  notes: row?.notes,
  deliveryMode: row?.delivery_mode,
  selectedEngineers: row?.selected_engineers || [],
  selectedLicenses: row?.selected_licenses || [],
  selectedMaterials: row?.selected_materials || [],
  selectedOutsource: row?.selected_outsource || [],
  config: row?.config || {},
  currency: row?.currency,
  calculations: row?.calculations || {},
  timestamp: row?.created_at,
  createdAt: row?.created_at,
  updatedAt: row?.updated_at,
});

export const getEstimatesFromDB = async (userId = null) => {
  let query = supabase?.from('estimates')?.select('*')?.order('created_at', { ascending: false })?.limit(50);
  if (userId) {
    query = query?.eq('user_id', userId);
  }
  const { data, error } = await query;
  if (error) throw error;
  return (data || [])?.map(fromEstimateRow);
};

export const saveEstimateToDB = async (estimateData, userId) => {
  const { data, error } = await supabase?.from('estimates')?.insert(toEstimateRow(estimateData, userId))?.select()?.single();
  if (error) throw error;
  return fromEstimateRow(data);
};

export const updateEstimateInDB = async (id, estimateData) => {
  const row = toEstimateRow(estimateData, undefined);
  // Remove user_id from update payload (don't change ownership)
  delete row?.user_id;
  const { data, error } = await supabase?.from('estimates')?.update(row)?.eq('id', id)?.select()?.single();
  if (error) throw error;
  return fromEstimateRow(data);
};

export const deleteEstimateFromDB = async (id) => {
  const { error } = await supabase?.from('estimates')?.delete()?.eq('id', id);
  if (error) throw error;
  return true;
};

export const searchEstimatesFromDB = async (query, userId = null) => {
  if (!query) return getEstimatesFromDB(userId);
  let dbQuery = supabase?.from('estimates')?.select('*')?.or(`name.ilike.%${query}%,project_name.ilike.%${query}%,notes.ilike.%${query}%`)?.order('created_at', { ascending: false })?.limit(50);
  if (userId) {
    dbQuery = dbQuery?.eq('user_id', userId);
  }
  const { data, error } = await dbQuery;
  if (error) throw error;
  return (data || [])?.map(fromEstimateRow);
};

export const getNextCalcNumber = async () => {
  const { data, error } = await supabase?.from('estimates')?.select('calc_number')?.not('calc_number', 'is', null)?.order('created_at', { ascending: false });

  if (error || !data || data?.length === 0) return 'Calc-001';

  let maxNum = 0;
  data?.forEach((row) => {
    const match = row?.calc_number?.match(/^Calc-(\d+)$/);
    if (match) {
      const num = parseInt(match?.[1], 10);
      if (num > maxNum) maxNum = num;
    }
  });
  return `Calc-${String(maxNum + 1)?.padStart(3, '0')}`;
};

// ============================================================
// VENDOR QUOTES SERVICE
// ============================================================

const toQuoteRow = (data, userId) => ({
  user_id: userId,
  quote_number: data?.quoteNumber,
  vendor_id: data?.vendorId || null,
  vendor_name: data?.vendorName,
  rfq_package_id: data?.rfqPackageId || null,
  rfq_package_name: data?.rfqPackageName || null,
  quote_date: data?.quoteDate || null,
  validity_date: data?.validityDate || null,
  lead_time_days: data?.leadTimeDays || 0,
  payment_terms: data?.paymentTerms || null,
  shipping_cost: data?.shippingCost || 0,
  currency: data?.currency || 'OMR',
  total_amount: data?.totalAmount || 0,
  status: data?.status || 'pending',
  notes: data?.notes || null,
  lines: data?.lines || [],
  attachments: data?.attachments || [],
});

const fromQuoteRow = (row) => ({
  id: row?.id,
  quoteNumber: row?.quote_number,
  vendorId: row?.vendor_id,
  vendorName: row?.vendor_name,
  rfqPackageId: row?.rfq_package_id,
  rfqPackageName: row?.rfq_package_name,
  quoteDate: row?.quote_date,
  validityDate: row?.validity_date,
  leadTimeDays: row?.lead_time_days,
  paymentTerms: row?.payment_terms,
  shippingCost: parseFloat(row?.shipping_cost) || 0,
  currency: row?.currency,
  totalAmount: parseFloat(row?.total_amount) || 0,
  status: row?.status,
  notes: row?.notes,
  lines: row?.lines || [],
  attachments: row?.attachments || [],
  createdAt: row?.created_at,
  updatedAt: row?.updated_at,
});

export const getVendorQuotes = async () => {
  const { data, error } = await supabase?.from('vendor_quotes')?.select('*')?.order('created_at', { ascending: false });
  if (error) throw error;
  return (data || [])?.map(fromQuoteRow);
};

export const saveVendorQuote = async (quoteData, userId) => {
  const { data, error } = await supabase?.from('vendor_quotes')?.insert(toQuoteRow(quoteData, userId))?.select()?.single();
  if (error) throw error;
  return fromQuoteRow(data);
};

export const updateVendorQuote = async (id, quoteData) => {
  const { data, error } = await supabase?.from('vendor_quotes')?.update({
      vendor_name: quoteData?.vendorName,
      rfq_package_id: quoteData?.rfqPackageId,
      rfq_package_name: quoteData?.rfqPackageName,
      quote_date: quoteData?.quoteDate,
      validity_date: quoteData?.validityDate,
      lead_time_days: quoteData?.leadTimeDays,
      payment_terms: quoteData?.paymentTerms,
      shipping_cost: quoteData?.shippingCost,
      currency: quoteData?.currency,
      total_amount: quoteData?.totalAmount,
      status: quoteData?.status,
      notes: quoteData?.notes,
      lines: quoteData?.lines,
      attachments: quoteData?.attachments,
    })?.eq('id', id)?.select()?.single();
  if (error) throw error;
  return fromQuoteRow(data);
};

export const deleteVendorQuote = async (id) => {
  const { error } = await supabase?.from('vendor_quotes')?.delete()?.eq('id', id);
  if (error) throw error;
  return true;
};
